import {
    NyuDataProperties,
    NyuSeriesProperties,
} from '../../../../shared/types'
import useChartPayload from '../../hooks/useChartPayload'
import { useHighContrastDisplay } from '../../hooks/useHighContrastMode'
import { toBaseDataKey } from '../../utils/helpers'
import { ResolvedReferenceLine } from '../../utils/referenceLines'
import DataKeyLabel from './DataKeyLabel'
import ForecastLabel from './ForecastLabel'
import { hatchColorOf } from './HatchPattern'
import { getLetterIdentifier } from './HighContrastMode/BarLabel'
import { SHAPES } from './HighContrastMode/constants'
import ReferenceLineLabel from './ReferenceLineLabel'

export default function CustomLegend({
    seriesProperties,
    dataProperties,
    referenceLines = [],
}: {
    seriesProperties: NyuSeriesProperties
    dataProperties: NyuDataProperties
    referenceLines?: ResolvedReferenceLine[]
}) {
    const payload = seriesProperties.map((series) => ({
        dataKey: series.key,
        value: undefined,
    }))

    const { dataKeys, getLineData } = useChartPayload({
        payload,
        seriesProperties,
    })

    const { showShapes, showLetters } = useHighContrastDisplay(
        dataProperties,
        seriesProperties
    )

    // a forecast listed in its own right explains its hatching itself
    const hasHatchedForecast =
        dataProperties.chartType === 'bar' &&
        seriesProperties.some(
            (series) =>
                hatchColorOf(series) !== undefined &&
                !dataKeys.includes(series.key)
        )

    return (
        <ul className="my-4 flex flex-row flex-wrap justify-start gap-3 sm:justify-center md:gap-6">
            {dataKeys.map((dataKey, index) => {
                // First, find the index in seriesProperties for this dataKey
                const seriesIndex = seriesProperties.findIndex(
                    (series) => series.key === dataKey
                )
                let shapeIndex = seriesIndex >= 0 ? seriesIndex : index

                const baseKey = toBaseDataKey(dataKey)
                if (baseKey !== dataKey) {
                    const baseSeriesIndex = seriesProperties.findIndex(
                        (series) => series.key === baseKey
                    )
                    if (baseSeriesIndex >= 0) {
                        shapeIndex = baseSeriesIndex
                    }
                }

                return (
                    <li key={dataKey}>
                        <DataKeyLabel
                            lineData={getLineData(dataKey)!}
                            highContrastMode={showShapes}
                            shapeType={
                                showShapes
                                    ? SHAPES[shapeIndex % SHAPES.length]
                                    : undefined
                            }
                            letterIdentifier={
                                showLetters
                                    ? getLetterIdentifier(
                                          dataKey,
                                          seriesProperties,
                                          dataKeys
                                      )
                                    : undefined
                            }
                            isBarChart={dataProperties.chartType === 'bar'}
                        />
                    </li>
                )
            })}
            {hasHatchedForecast && (
                <li>
                    <ForecastLabel />
                </li>
            )}
            {referenceLines
                .filter(
                    (referenceLine) =>
                        referenceLine.label && !referenceLine.hideFromLegend
                )
                .map((referenceLine) => (
                    <li key={referenceLine.id}>
                        <ReferenceLineLabel referenceLine={referenceLine} />
                    </li>
                ))}
        </ul>
    )
}
